import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { activeTableSortLabel } from '../../../../config/MaterialUIConstants';
import ServiceItem from './ServiceItem';

// Styles object matching VariantSelectionModal
const styles = {
    variantsContainer: {
        // minHeight: '200px',
        maxHeight: '350px',
        overflow: 'auto',
        borderRadius: '0px!important',
        // border: '1px solid #E6E6E6',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            margin: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
            },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.3) rgba(0,0,0,0.1)',
    },
    table: {
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
        '& .MuiTableCell-root': {
            fontSize: '16px',
        },
        // Remove border-spacing from header row
        '& thead tr': {
            marginBottom: '0',
        },
    },
    headerCell: {
        backgroundColor: '#F8F8F8',
        fontWeight: 500,
        borderTop: '1px solid #E6E6E6',
        padding: '8px 12px',
    },
    headerCellLast: {
        backgroundColor: '#F8F8F8',
        fontWeight: 500,
        borderTop: '1px solid #E6E6E6',
        borderRight: '1px solid #E6E6E6',
        borderLeft: 'none',
        padding: '12px 12px',
    },
};

const ServicesList = ({
    savedServiceParams,
    transformedServices,
    expandedServices,
    loading,
    hasMore,
    savedServiceLoading,
    handleSortingChange,
    onExpandToggle,
    onServiceToggle,
    onLoadMore,
    onEditService,
    serviceListPayload = [],
}) => {
    const scrollTimeoutRef = useRef(null);
    const containerRef = useRef(null);

    // Custom scroll handler for infinite scroll
    const handleScroll = useCallback(
        (event) => {
            const listboxNode = event.currentTarget;
            const scrollPosition = listboxNode.scrollTop;
            const scrollBottom = scrollPosition + listboxNode.clientHeight >= listboxNode.scrollHeight - 50; // Increased threshold

            if (scrollBottom && hasMore && !savedServiceLoading) {
                console.log('Triggering load more - scrollBottom:', scrollBottom, 'hasMore:', hasMore, 'loading:', savedServiceLoading);

                // Clear any existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Add a small delay to prevent rapid fire API calls
                scrollTimeoutRef.current = setTimeout(() => {
                    if (!savedServiceLoading && hasMore) {
                        console.log('Calling onLoadMore');
                        onLoadMore();
                    }
                    scrollTimeoutRef.current = null;
                }, 100); // Reduced delay for better UX
            }
        },
        [hasMore, savedServiceLoading, onLoadMore]
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);
    return (
        <Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}>
                    <CircularProgress />
                </Box>
            ) : transformedServices.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}>
                    <Typography variant="body1" color="text.secondary">
                        No saved services found
                    </Typography>
                </Box>
            ) : (
                <Box onScroll={handleScroll} ref={containerRef}>
                    <TableContainer elevation={0} sx={styles.variantsContainer}>
                        <Table stickyHeader sx={styles.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...styles.headerCell, borderLeft: '1px solid #E6E6E6', paddingRight: '0' }} width={30}>
                                        {/* Empty for collapse button */}
                                    </TableCell>

                                    <TableCell width={200} sx={styles.headerCell}>
                                        <TableSortLabel
                                            active={true}
                                            direction={savedServiceParams.sortBy === 'serviceName' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                            onClick={() => handleSortingChange('serviceName')}
                                            sx={savedServiceParams.sortBy === 'serviceName' && activeTableSortLabel}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell width={120} sx={styles.headerCell}>
                                        <TableSortLabel
                                            active={true}
                                            direction={
                                                savedServiceParams.sortBy === 'shopServiceReference' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'
                                            }
                                            onClick={() => handleSortingChange('shopServiceReference')}
                                            sx={savedServiceParams.sortBy === 'shopServiceReference' && activeTableSortLabel}
                                        >
                                            Reference
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell width={120} sx={styles.headerCell}>
                                        Make
                                    </TableCell>
                                    <TableCell id="parts-table-cell" width={80} sx={styles.headerCell} align="center">
                                        Parts
                                    </TableCell>
                                    <TableCell id="labour-table-cell" width={80} sx={styles.headerCell} align="center">
                                        Labour
                                    </TableCell>
                                    <TableCell id="amount-table-cell" align="left" width={110} sx={styles.headerCellLast}>
                                        Amount
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transformedServices.map((service) => (
                                    <ServiceItem
                                        key={service.id}
                                        service={service}
                                        isExpanded={expandedServices.includes(service.id)}
                                        onExpandToggle={onExpandToggle}
                                        onServiceToggle={onServiceToggle}
                                        onEditService={onEditService}
                                        serviceListPayload={serviceListPayload}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Loading indicator for infinite scroll */}
                    {savedServiceLoading && hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Loading more services...
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ServicesList;
